import {
  onEvent,
  TRACK_UDP_SET_USER_ID,
  TRACK_UDP_UNSET_USER_ID,
  triggerEvent,
} from '@fec/assets/js/utils/event';

import {
  User,
  ConfigUserProvider,
  AuthenticationService,
} from 'cidaas-javascript-sdk';

import { WebStorageStateStore } from 'oidc-client-ts';
import {
  SRG_LOGIN_CHECK_SESSION,
  SRG_LOGIN_INITIAL,
  SRG_LOGIN_LOGGED_IN,
  SRG_LOGIN_LOGGED_OUT,
  SRG_LOGIN_OPEN_PROFILE_PAGE,
  SRG_LOGIN_TRIGGER_LOGIN,
  SRG_LOGIN_TRIGGER_LOGOUT,
  SRG_LOGIN_TRIGGER_REGISTRATION,
} from '@aron/srg-login/SrgLoginEvents';

const OIDC_QUERY_PARAMETERS = [
  'response_type',
  'client_id',
  'redirect_uri',
  'scope',
  'nonce',
  'prompt',
  'state',
  'ui_locales',
  'request',
  'code',
  'session_state',
  'expires_in',
];

const CIDAAS_QUERY_PARAMETERS = [
  'view_type', // https://github.com/Cidaas/cidaas-javascript-sdk/blob/7f35787d36cae492ed6409aaffabbfdc3189eebc/src/main/authentication/Authentication.ts#L38
  'viewtype',
  'iss',
];

/**
 * The SrfSrgLogin class encapsulates the login, logout, and registration flows for the SRG Login (Cidaas) service.
 */
export class SrfSrgLogin {
  private cidaasConfigUserProvider: ConfigUserProvider;
  private authenticationService: AuthenticationService;

  /**
   * Construct a new instance of the SrfSrgLogin class.
   * @param authority The OIDC authority URL of the SRG Login service.
   * @param clientId The OIDC client ID.
   * @param scopes The OIDC scopes that will be requested.
   * @param bu The business unit for which the login is being performed. This will determine the language of the login UI.
   */
  constructor(
    authority: string,
    clientId: string,
    scopes: string,
    bu: 'srf' | 'rtr' = 'srf',
  ) {
    this.cidaasConfigUserProvider = new ConfigUserProvider({
      authority,
      client_id: clientId,
      redirect_uri: window.location.href,
      post_logout_redirect_uri: window.location.href,
      scope: scopes,
      ui_locales: bu === 'rtr' ? 'rm' : 'de',
      userStore: new WebStorageStateStore({ store: window.localStorage }),
    });

    this.authenticationService = new AuthenticationService(
      this.cidaasConfigUserProvider,
    );

    this.checkReturnFromWebLogin();
    this.registerListener();
  }

  /**
   * Register various event listeners for events sent from feature components.
   */
  registerListener() {
    onEvent({
      eventName: SRG_LOGIN_CHECK_SESSION,
      eventHandler: async () => {
        await this.checkExistingSession();
      },
    });

    onEvent({
      eventName: SRG_LOGIN_TRIGGER_LOGIN,
      eventHandler: async () => {
        await this.login();
      },
    });

    onEvent({
      eventName: SRG_LOGIN_TRIGGER_REGISTRATION,
      eventHandler: async () => {
        await this.register();
      },
    });

    onEvent({
      eventName: SRG_LOGIN_TRIGGER_LOGOUT,
      eventHandler: async () => {
        await this.logout();
      },
    });

    onEvent({
      eventName: SRG_LOGIN_OPEN_PROFILE_PAGE,
      eventHandler: () => {
        this.openUserProfile();
      },
    });
  }

  /**
   * Starts the login flow via the browser.
   * If the user is already logged in, nothing happens.
   */
  async login() {
    if (await this.isLoggedIn()) {
      return;
    }
    await this.authenticationService.loginWithBrowser();
  }

  async logout() {
    if (!(await this.isLoggedIn())) {
      return;
    }
    triggerEvent(TRACK_UDP_UNSET_USER_ID);
    triggerEvent(SRG_LOGIN_LOGGED_OUT);
    await this.authenticationService.logout();
  }

  async register() {
    await this.authenticationService.registerWithBrowser();
  }

  /**
   * Opens the users profile page in the browser where the user can edit their details, such as name, email, etc.
   */
  openUserProfile() {
    window.location.href = this.cidaasConfigUserProvider.getConfig().authority;
  }

  /**
   * Handles the case where the user has logged in via the web, or was already logged in previously.
   * @param user The user which has logged in.
   */
  async handleLoggedInFromWeb(user: User) {
    const { profile } = user;
    const { sub: userId } = profile;

    triggerEvent(TRACK_UDP_SET_USER_ID, userId as any);
    triggerEvent(SRG_LOGIN_LOGGED_IN, {
      user,
    } as any);
  }

  /**
   * Handles the case where the user has returned from the web login flow (redirect via SRG Login pages).
   * @returns
   */
  async checkReturnFromWebLogin() {
    try {
      const user = await this.authenticationService.loginCallback();

      if (!user) {
        return;
      }

      await this.handleLoggedInFromWeb(user);
      triggerEvent(SRG_LOGIN_INITIAL, {
        user,
      } as any);
    } catch (err) {
      // swallowed
    } finally {
      this.cleanupUrlPostLogin();
    }
  }

  /**
   * Checks whether the user had previously logged in. If they did, then the user is logged in again.
   */
  async checkExistingSession() {
    const user = await this.authenticationService.getUserInfoFromStorage();
    if (user && this.userHasValidSession(user)) {
      await this.handleLoggedInFromWeb(user);
    }
  }

  /**
   * Cleans the URL after a browser-based login flow to remove any query parameters that were used during the login flow.
   */
  cleanupUrlPostLogin() {
    const url = new URL(window.location.href);
    const searchParams = url.searchParams;

    [...OIDC_QUERY_PARAMETERS, ...CIDAAS_QUERY_PARAMETERS].forEach((param) => {
      searchParams.delete(param);
    });

    window.history.replaceState({}, document.title, url.toString());
  }

  userHasValidSession(user: User | null) {
    return user && !user.expired;
  }

  async isLoggedIn() {
    const user = await this.authenticationService.getUserInfoFromStorage();
    return this.userHasValidSession(user);
  }
}
